import { createContext, FC, ReactNode, useContext, useState } from 'react'

import { AdvanceAmountSchema } from '@/containers/onDemandPay/TakeAdvance/lib/schemas/advance-amount-schema'
import { AdvanceConfirmationFormSchema } from '@/containers/onDemandPay/TakeAdvance/lib/schemas/advance-confirmation-schema'
import { CardSelectionSchema } from '@/containers/onDemandPay/TakeAdvance/lib/schemas/card-selection-schema'
import { AdvanceStatus } from '@/types/ewa'

import { AdvanceAmountContent } from './steps/amount/AdvanceAmountContent'
import { AdvanceConfirmationContent } from './steps/confirmation/AdvanceConfirmation'
import { AdvanceDestinationContent } from './steps/destination/AdvanceDestinationContent'
import { AdvanceResultContent } from './steps/result/AdvanceResult'

export enum AdvanceStep {
  Destination = 'AdvanceDestination',
  Amount = 'AdvanceAmount',
  Confirmation = 'AdvanceConfirmation',
  Result = 'AdvanceResult',
}

export const ADVANCE_STEPS = [
  AdvanceStep.Destination,
  AdvanceStep.Amount,
  AdvanceStep.Confirmation,
  AdvanceStep.Result,
]

export const steps = {
  [AdvanceStep.Amount]: AdvanceAmountContent,
  [AdvanceStep.Confirmation]: AdvanceConfirmationContent,
  [AdvanceStep.Destination]: AdvanceDestinationContent,
  [AdvanceStep.Result]: AdvanceResultContent,
}

export const titles = {
  [AdvanceStep.Amount]: 'Amount',
  [AdvanceStep.Confirmation]: 'Confirmation',
  [AdvanceStep.Destination]: 'Destination',
  [AdvanceStep.Result]: '',
}

type Step = AdvanceStep

type Props = {
  children: ReactNode
}

export type TakeAdvanceFormValues = CardSelectionSchema &
  AdvanceAmountSchema &
  AdvanceConfirmationFormSchema & {
    transfer_id: string
    status: AdvanceStatus
  }

type TakeAdvanceContextProps = {
  nextStep: () => void
  backStep: () => void
  resetStep: () => void
  previousStep: Step
  currentStep: Step
  reset: () => void
  close: () => void
  setModalOpen: (open: boolean) => void
  modalOpen: boolean
  takeAdvanceValues: TakeAdvanceFormValues
  setTakeAdvanceValues: (values: TakeAdvanceFormValues) => void
}

const TakeAdvanceContext = createContext<TakeAdvanceContextProps | undefined>(
  undefined
)

const defaultValues: TakeAdvanceFormValues = {
  card_type: 'EWA_CARD',
  advance_amount: '',
  signature: '',
  transfer_id: '',
  status: 'po pending',
}

export const TakeAdvanceProvider: FC<Props> = ({ children }) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<Step>(AdvanceStep.Destination)
  const [previousStep, setPreviousStep] = useState<Step>(
    AdvanceStep.Destination
  )
  const [takeAdvanceValues, setTakeAdvanceValues] =
    useState<TakeAdvanceFormValues>(defaultValues)

  const nextStep = () => {
    const currentIndex = ADVANCE_STEPS.indexOf(currentStep)
    if (currentIndex < ADVANCE_STEPS.length - 1) {
      const nextStep = ADVANCE_STEPS[currentIndex + 1]
      setCurrentStep(nextStep)
      setPreviousStep(currentStep)
    }
  }

  const backStep = () => {
    const currentIndex = ADVANCE_STEPS.indexOf(currentStep)
    if (currentIndex > 0) {
      const prevStep = ADVANCE_STEPS[currentIndex - 1]
      setCurrentStep(prevStep)
      setPreviousStep(currentStep)
    }
  }

  const resetStep = () => {
    setCurrentStep(AdvanceStep.Destination)
    setPreviousStep(AdvanceStep.Destination)
  }

  const reset = () => {
    setTakeAdvanceValues(defaultValues)
  }

  const close = () => {
    resetStep()
    reset()
    setModalOpen(false)
  }

  return (
    <TakeAdvanceContext.Provider
      value={{
        modalOpen,
        setModalOpen,
        currentStep,
        previousStep,
        reset,
        takeAdvanceValues,
        nextStep,
        backStep,
        resetStep,
        close,
        setTakeAdvanceValues,
      }}
    >
      {children}
    </TakeAdvanceContext.Provider>
  )
}

export const useTakeAdvance = () => {
  const context = useContext(TakeAdvanceContext)
  if (!context) {
    throw new Error('useTakeAdvance must be used within a TakeAdvanceProvider')
  }
  return context
}
